<template>
  <Dialog :open="visibility" @close="handleDialogClose" show-close="true">
    <DialogOverlay class="fixed inset-0 bg-black-1/60 z-[99]" />
    <div class="login-dialog-wrapper shadow fixed inset-0 m-auto z-[999]">
      <div v-if="currentMode == 0">
        <!-- 统一头部 -->
        <div class="dialog-header">
          <h1 class="!font-normal">账号登录</h1>
          <button @click="handleDialogClose"
            class="absolute top-2.5 right-2.5 mx-auto z-10 w-6 h-6 rounded hover:bg-black-1 bg-[#F3F4F9]">
            <img class="m-auto" src="@assets/images/Icon_del.png" />
          </button>
        </div>

        <!-- 登录/注册切换 -->
        <div class="login-tabs">
          <button @click="changeLoginMethod(0)" :class="{ active: loginMethod === 0 }">
            手机登录
          </button>
          <button @click="changeLoginMethod(1)" :class="{ active: loginMethod === 1 }">
            邮箱登录
          </button>
        </div>

        <!-- 手机登录 -->
        <div class="form-content" v-if="loginMethod == 0">
          <div>
            <div v-if="isSmsStep" class="field-label">手机号</div>
            <div class="input-group phone-input">
              <select name="phone-select" id="phone-select">
                <option v-for="(item, value) in phoneOtions" :key="value" :value="item.value">
                  {{ item.content }}
                </option>
              </select>
              <StandardInput class="flex-1" v-model="loginForm.mobile" type="tel" :maxlength="11"
                placeholder="请输入手机号" />
            </div>
          </div>
          <div>
            <div v-if="isSmsStep" class="field-label">
              手机验证（{{ maskedSentMobile }}）
            </div>
            <div class="input-group">
              <StandardInput class="flex-1" v-model="loginForm.code" type="text" :maxlength="6"
                placeholder="请输入验证码" @keyup.enter="mobileLogin" />
              <button class="code-btn" @click="getMobileCode" :disabled="smsTimeController.disabled">
                {{ smsTimeController.label }}
              </button>
            </div>
          </div>
          <div class="agreement">
            <el-checkbox v-model="isAgree" label="" size="large" />
            <span>我同意
              <router-link target="_blank" :class="['link', { underline: isAgree }]" :to="'/agreement'">
                维度工厂用户协议
              </router-link>
              和
              <router-link target="_blank" :class="['link', { underline: isAgree }]" :to="{ name: 'WidePrivacy' }">
                维度工厂隐私协议
              </router-link>
            </span>
          </div>
          <button class="main-action-btn" @click="mobileLogin">
            注册并登录
          </button>
        </div>

        <!-- 邮箱登录 -->
        <div class="form-content step2" v-else-if="loginMethod == 1">
          <div>
            <div class="mb-2.5 text-sm text-[#737380]">已注册邮箱</div>
            <div class="input-group">
              <StandardInput v-model="loginForm.email" name="email" autocomplete="username"
                placeholder="请输入邮箱地址" class="flex-1" />
            </div>
          </div>
          <div>
            <div class="mb-2.5 text-sm text-[#737380]">密码</div>
            <div class="input-group">
              <StandardInput v-model="loginForm.password" name="password" type="password"
                autocomplete="current-password" placeholder="请输入密码" :minlength="8" :maxlength="18"
                @keyup.enter="login" class="flex-1" />
            </div>
          </div>
          <div class="options">
            <el-checkbox v-model="isRemember" label="记住我，保持登录状态" size="large" />
            <!-- <button class="link" @click="currentMode = 2">忘记密码</button> -->
          </div>
          <button class="main-action-btn" @click="login" :disabled="!isEmailLoginEnabled">
            登录
          </button>
        </div>
      </div>

      <!-- 忘记密码 -->
      <div class="form-content" v-else-if="currentMode == 2">
        <div class="w-full text-3xl text-center text-[#222226]">忘记密码</div>
        <div class="input-group">
          <StandardInput class="flex-1" v-model="resetForm.emailCode" placeholder="输入验证码" />
          <button class="code-btn" @click="getMobileCode" :disabled="codeDelay > 0">
            {{ codeDelay > 0 ? `${codeDelay}s` : "获取验证码" }}
          </button>
        </div>
        <StandardInput v-model="resetForm.email" placeholder="输入邮箱" />
        <StandardInput v-model="resetForm.password" type="password" :maxlength="18" placeholder="输入8-18位新密码 注意区分大小写" />
        <StandardInput v-model="resetForm.checked" type="password" :maxlength="18" placeholder="确认新密码" @keyup.enter="reset" />
        <button class="py-2 rounded bg-purple-600 text-white text-sm"
          style="width: 150px; letter-spacing: 5px; margin-left: 72px" @click="reset">
          修改
        </button>
        <button
          class="ml-2 border-opacity-0 border-purple-600 border-b align-bottom text-purple-600 text-xs hover:border-opacity-100"
          @click="currentMode = 0">
          使用现有账户
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import axios from "axios";
import { reactive, ref, watch, computed, onUnmounted } from "vue";
import { defHttp, noTokenHttp } from "@utils/http";
import { Dialog, DialogOverlay } from "@headlessui/vue";
import StandardInput from "./StandardInput.vue";
import { setToken } from "@utils/user";
// import { notification } from "../utils/notification";

import { ElMessage } from "element-plus";
import useStore from "@store";
import {
  createCountdownController,
  SMS_CODE_ENDPOINTS,
  sendSmsWithGuard,
} from "@/utils/smsCaptchaGuard";
const { personalStore, searchStore } = useStore();

const phoneOtions = [
  {
    value: "+86",
    content: "+86",
  },
];

const props = defineProps({
  visibility: Boolean,
  mode: { type: Number, default: 0 },
});
const currentMode = ref(props.mode);

// 监听props.mode变化，同步到currentMode
watch(
  () => props.mode,
  (newMode) => {
    currentMode.value = newMode;
  },
);
const emit = defineEmits(["update:visibility"]);
const loginForm = reactive({
  email: "",
  password: "",
  mobile: "",
  code: "",
});
let isRemember = ref(false); //请记住我
let isAgree = ref(false); // 请同意
const rememberedLogin = localStorage.getItem("loginForm");
if (rememberedLogin) {
  try {
    const savedLogin = JSON.parse(rememberedLogin);
    loginForm.email = savedLogin.email || "";
    loginForm.password = savedLogin.password || "";
    isRemember.value = !!loginForm.email && !!loginForm.password;
  } catch (error) {
    localStorage.removeItem("loginForm");
  }
}
const registerForm = reactive({
  email: "",
  emailCode: "",
  password: "",
  checked: "",
});
const resetForm = reactive({
  email: "",
  emailCode: "",
  password: "",
  checked: "",
});
//邮箱验证码
const codeDelay = ref(0);
let codeDelayInterval = null;
function getRegCode() {
  noTokenHttp
    .post("/api/register/getRandom", { email: registerForm.email })
    .then(() => {
      codeDelay.value = 59;
      codeDelayInterval = setInterval(() => {
        if (codeDelay.value > 0) {
          codeDelay.value--;
        } else {
          clearInterval(codeDelayInterval);
        }
      }, 1000);
    });
}
function getResetCode() {
  noTokenHttp
    .post("/api/reset/getRandom", { email: resetForm.email })
    .then(() => {
      codeDelay.value = 59;
      codeDelayInterval = setInterval(() => {
        if (codeDelay.value > 0) {
          codeDelay.value--;
        } else {
          clearInterval(codeDelayInterval);
        }
      }, 1000);
    });
}
function remember() {
  localStorage.setItem("loginForm", JSON.stringify({
    email: loginForm.email,
    password: loginForm.password,
  }));
}
// 改变登录方式
const loginMethod = ref(0);
function changeLoginMethod(v) {
  loginMethod.value = v;
}
let time = ref(0);
let text = ref("");
const isSmsStep = ref(false);
const sentMobile = ref("");
const smsTimeController = createCountdownController(time, { actionKey: "login", mobile: () => loginForm.mobile.trim() });
const mobileRegex = /^1[3-9]\d{9}$/;

function isValidMobile(mobile) {
  return mobileRegex.test(String(mobile).trim());
}

const maskedSentMobile = computed(() => {
  if (!sentMobile.value) return "";
  const mobile = String(sentMobile.value).replace(/\s/g, "");
  if (mobile.length < 4) return mobile;
  return `***${mobile.slice(-4)}`;
});

// 计算属性 - 邮箱登录按钮启用状态
const isEmailLoginEnabled = computed(() => {
  const passwordLength = loginForm.password.length;
  return !!loginForm.email.trim() && passwordLength >= 8 && passwordLength <= 18;
});

function clearSmsTimer() {
  smsTimeController.clear();
}

function resetDialogState() {
  clearSmsTimer();
  currentMode.value = 0;
  loginMethod.value = 0;
  loginForm.mobile = "";
  loginForm.code = "";
  isAgree.value = false;
  time.value = 0;
  text.value = "";
  isSmsStep.value = false;
  sentMobile.value = "";
}

function handleDialogClose() {
  resetDialogState();
  emit("update:visibility", false);
}

watch(
  () => props.visibility,
  (visible) => {
    if (!visible) {
      resetDialogState();
    }
  },
);

// 手机登录
function mobileLogin() {
  const mobile = loginForm.mobile.trim();
  if (!mobile) {
    ElMessage({ message: "请输入手机号", type: "warning" });
    return;
  }

  if (!isValidMobile(mobile)) {
    ElMessage({ message: "请输入正确的手机号", type: "warning" });
    return;
  }

  const code = loginForm.code.trim();
  if (!code) {
    ElMessage({ message: "请输入验证码", type: "warning" });
    return;
  }

  if (code.length > 6) {
    ElMessage({ message: "请输入不超过6位的验证码", type: "warning" });
    return;
  }

  if (!isAgree.value) {
    ElMessage({ message: "请先同意用户协议和隐私协议", type: "warning" });
    return;
  }

  loginForm.mobile = mobile;
  loginForm.code = code;
  noTokenHttp.post("/api/mobileLogin", loginForm).then((res) => {
    if (res.data.code == 200) {
      // 后端 token 有效期已延长到 1 年：手机登录也默认写入可持久化缓存
      setToken("Bearer " + res.data.token, true);
      defHttp.post("/api/user-api/user/get").then(function (res) {
        const data = res.data.data;
        const userInfo = data;
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
        emit("update:visibility", false);
        localStorage.setItem(
          "username",
          res.data.data.nickName || res.data.data.userName,
        );

        //  store
        personalStore.$reset();
        searchStore.$reset();

        ElMessage.success("登录成功");
        setTimeout(() => {
          location.reload();
        }, 300);
      });
    } else {
      ElMessage.error(res.data.msg || '网络异常');
    }
  });
}
// 获取手机验证码
async function getMobileCode() {
  if (!isValidMobile(loginForm.mobile)) {
    ElMessage({ message: "请输入正确的手机号", type: "warning" });
    return;
  }

  loginForm.mobile = loginForm.mobile.trim();
  const requestedMobile = loginForm.mobile;

  try {
    const res = await sendSmsWithGuard({
      actionKey: "login",
      mobile: requestedMobile,
      title: "安全验证",
      description: "拖动下方滑块完成拼图",
      controller: smsTimeController,
      sendRequest: ({ payload }) =>
        noTokenHttp.post(SMS_CODE_ENDPOINTS.LOGIN, {
          ...payload,
        }, {
          skipGlobalErrorMessage: true,
        }),
    });

    if (loginForm.mobile !== requestedMobile) return;
    if (res.data.code == 200) {
      sentMobile.value = loginForm.mobile;
      isSmsStep.value = true;
      text.value = res.data.msg;
    } else if (
      res.data.code == 429 &&
      Number(res.data.data?.retryAfterSeconds) > 0
    ) {
      // 已进入短信校验流程时，限流只阻止重新发送，不应退回手机号输入页。
      sentMobile.value = loginForm.mobile;
      isSmsStep.value = true;
      text.value = res.data.msg;
    } else {
      if (!res.smsFeedbackShown) ElMessage({ message: res.data.msg, type: "warning" });
      text.value = "";
    }
  } catch (error) {
    if (error?.message !== "graphic captcha closed") {
      console.log(error);
      ElMessage({ message: "验证码发送失败，请稍后再试", type: "warning" });
    }
  }
}
// 邮箱登录
function login() {
  if (loginForm.password.length < 8 || loginForm.password.length > 18) {
    ElMessage.warning("请输入8-18位密码");
    return;
  }

  noTokenHttp.post("/api/login", loginForm).then((res) => {
    const code = res.data.code;
    console.log(res);
    if (code == 200) {
      if (isRemember.value == true) {
        remember();
        setToken("Bearer " + res.data.token, true);
      } else {
        localStorage.removeItem("loginForm");
        setToken("Bearer " + res.data.token, false);
      }
      console.log(res, 1);
      defHttp.post("/api/user-api/user/get").then(function (res) {
        console.log(res, res.data.data.userName, 2222);
        const data = res.data.data;
        const userInfo = data;
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
        emit("update:visibility", false);
        localStorage.setItem(
          "username",
          res.data.data.nickName || res.data.data.userName,
        );

        // 初始化store
        personalStore.$reset();
        searchStore.$reset();

        ElMessage.success("登录成功");
        setTimeout(() => {
          location.reload();
        }, 300);
      });
    } else {
      ElMessage({
        message: res.data.msg || '网络异常',
        type: "error",
      });
    }
  });
}
function register() {
  console.log(registerForm);
  if (
    !registerForm.checked ||
    !registerForm.email ||
    !registerForm.password ||
    !registerForm.emailCode
  ) {
    ElMessage({ message: "请补全信息", type: "warning" });
    return false;
  }
  if (registerForm.checked !== registerForm.password) {
    ElMessage({ message: "两次输入的密码不一致", type: "warning" });
    return false;
  }
  noTokenHttp.post("/api/register", registerForm).then((res) => {
    if (res.data.code == 200) {
      emit("update:visibility", false);
      location.reload();
    } else {
      ElMessage({ message: res.data.msg || '网络异常', type: "error" });
    }
  });
}

onUnmounted(() => {
  clearSmsTimer();
});
function reset() {
  console.log(resetForm);

  if (
    !resetForm.checked ||
    !resetForm.email ||
    !resetForm.password ||
    !resetForm.emailCode
  ) {
    ElMessage({ message: "请补全信息", type: "warning" });
    return false;
  }
  if (resetForm.password.length < 8 || resetForm.password.length > 18) {
    ElMessage.warning("请输入8-18位密码");
    return false;
  }
  if (resetForm.checked !== resetForm.password) {
    ElMessage({ message: "两次输入的密码不一致", type: "warning" });
    return false;
  }
  noTokenHttp.post("/api/reset", resetForm).then((res) => {
    if (res.data.code == 200) {
      ElMessage({ message: "重置成功", type: "success" });
      setTimeout(() => {
        location.reload();
        emit("update:visibility", false);
      }, 1000);
    } else {
      ElMessage({ message: res.data.msg || '网络异常', type: "error" });
    }
  });
}
</script>

<style lang="scss" scoped>
.login-dialog-wrapper {
  width: 390px;
  height: 538px;
  background: #fff;
  border-radius: 8px;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;

  h1 {
    color: #222226;
    font-size: 32px;
    font-weight: bold;
    line-height: 1;
  }
}

.login-tabs {
  display: inline-flex;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 30px;
  width: 152px;

  button {
    width: 86px;
    height: 32px;
    padding: 0;
    border: none;
    background-color: #fff;
    color: #47496f;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;

    &:not(:first-child) {
      border-left: 1px solid #e0e0e0;
    }

    &.active {
      background-color: #47496f;
      color: #ffffff;
    }
  }
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
  flex: 1;
}

.form-content.step2 {
  margin-top: -10px;
  gap: 20px;
}

.field-label {
  margin-bottom: 10px;
  color: #737380;
  font-size: 14px;
}

.input-group {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  height: 46px;
  overflow: hidden;

  :deep(input) {
    border: none !important;
    height: 100%;
    box-shadow: none !important;
    padding-left: 16px;

    &::placeholder {
      color: #737380;
    }
  }
}

.phone-input {
  #phone-select {
    background-color: #f3f4f9;
    border: none;
    padding: 0 20px;
    height: 100%;
    color: #737380;
    outline: none;
    appearance: none; // Hides default arrow
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23737380' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
    border-right: 1px solid #c9cad6;
    font-size: 14px;
  }

  :deep(input) {
    padding-left: 10px;
  }
}

.code-btn {
  width: 130px;
  min-width: 130px;
  flex-shrink: 0;
  background: #47496f;
  color: #fff;
  border: none;
  padding: 0 15px;
  height: 100%;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    background-color: #f3f4f9;
    cursor: not-allowed;
    color: #222226;
  }
}

.agreement {
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: 14px;
    color: #737380;
  }

  .link {
    color: #47496f;
    font-weight: bold;
    text-decoration: none;
  }

  .link.underline {
    text-decoration: underline;
  }

  :deep(.el-checkbox) {
    height: auto;
  }

  :deep(.el-checkbox__inner) {
    border-radius: 50%;
  }
}

.options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  :deep(.el-checkbox__label) {
    color: #737380;
  }

  .link {
    color: #47496f;
    font-weight: normal;
    font-size: 14px;
  }

  :deep(.el-checkbox__inner) {
    border-radius: 50%;
  }
}

.agreement,
.options {
  :deep(.el-checkbox__input:not(.is-checked) .el-checkbox__inner) {
    background-color: #fff;
    border-color: #737380;
  }

  :deep(.el-checkbox__input:not(.is-checked):hover .el-checkbox__inner),
  :deep(.el-checkbox__input.is-focus:not(.is-checked) .el-checkbox__inner) {
    border-color: #47496f;
  }
}

.main-action-btn {
  height: 50px;
  width: 100%;
  background: #47496f;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: normal;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 27px;

  &:disabled {
    background-color: #d1d1d1;
    cursor: not-allowed;
  }
}

// Cleanup old styles
.login-wrapper {
  padding: 0; // Reset padding
  border: none; // Reset border
}
</style>
